import {
  currentMeasurements,
  type DataSeries,
  type DeliveryPoint,
  type Site,
  type SiteDashboard,
} from "@syllarys/contracts";
import type { DayWindow } from "./siteDashboard";

/** Ne pas publier un total incomplet comme un total exhaustif. */
export function sumKnown(values: Array<number | null>): number | null {
  if (!values.length) return null;
  let total = 0;
  for (const value of values) {
    if (value === null) return null;
    total += value;
  }
  return total;
}

/** Un état nominal historique n'est pas une preuve de collecte. */
export function contextualSiteStatus(
  site: Site,
  points: DeliveryPoint[],
): Site["status"] {
  if (site.dataAccess === "context") return "unknown";
  if (site.status !== "normal") return site.status;
  const hasPoint = points.some(
    (point) => point.siteId === site.id && point.status === "active",
  );
  if (
    !hasPoint ||
    site.annualMwh === null ||
    (site.annualCoverage?.missingDays ?? 0) > 0 ||
    (site.annualCoverage?.pendingPoints ?? 0) > 0
  )
    return "unknown";
  return site.status;
}

/** Les agrégats journaliers ne prouvent pas la complétude de chaque compteur. */
export function dashboardQuality(
  dashboard: SiteDashboard | undefined,
  window: DayWindow,
): "missing" | "partial" | "unknown" {
  if (!dashboard || dashboard.electricityPoints + dashboard.gasPoints === 0)
    return "missing";
  const days = dashboard.days.filter(
    (day) => day.day >= window.from && day.day <= window.to,
  );
  if (!days.some((day) => day.electricityKwh !== null || day.gasKwh !== null))
    return "missing";
  const expectedDays =
    Math.round((Date.parse(window.to) - Date.parse(window.from)) / 86_400_000) +
    1;
  if (
    days.length < expectedDays ||
    days.some(
      (day) =>
        (dashboard.electricityPoints > 0 && day.electricityKwh === null) ||
        (dashboard.gasPoints > 0 && day.gasKwh === null),
    )
  )
    return "partial";
  return "unknown";
}

/** Nature des valeurs courantes, sans masquer un mélange mesuré/estimé/corrigé. */
export function seriesDataNature(
  series: DataSeries | undefined,
): "measured" | "estimated" | "corrected" | "synthetic" | "mixed" | "unknown" {
  if (!series) return "unknown";
  if (series.synthetic) return "synthetic";
  const qualities = new Set(
    currentMeasurements(series)
      .filter((value) => value.value !== null && value.quality !== "missing")
      .map((value) => value.quality),
  );
  if (qualities.size === 0) return "unknown";
  if (qualities.size > 1) return "mixed";
  const quality = [...qualities][0];
  return quality === "measured" ||
    quality === "estimated" ||
    quality === "corrected"
    ? quality
    : "unknown";
}
